/**
 * Taux de pénétration dans le temps.
 *
 * C'est le KPI du Google Sheet de prod : abonnés payants rapportés aux comptes
 * existants. Une seule série, donc pas de légende.
 *
 * ── La seule courbe MESURÉE de cette page ───────────────────────────────────
 * Elle sort de `daily_snapshots`, un relevé quotidien. Tout le reste de la
 * section est RECONSTITUÉ depuis les dates d'abonnement de la projection.
 *
 * Série potentiellement trouée : un jour sans snapshot est un jour où le sync a
 * échoué, et cette donnée est définitivement perdue. `connectNulls` reste à
 * `false` — une interruption dit « pas de mesure », là où un trait continu
 * inventerait une valeur.
 */

import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { useChartMarkers } from '../hooks/useChartMarkers';
import { formatDayLong, formatDayShort, formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DataColumn, PenetrationPoint } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';
import { MarkerStrip } from './MarkerStrip';

const columns: ReadonlyArray<DataColumn<PenetrationPoint>> = [
  { key: 'date', header: 'Jour', render: (row) => formatDayLong(row.date) },
  { key: 'accounts', header: 'Comptes', numeric: true, render: (row) => formatInteger(row.totalAccounts) },
  { key: 'subs', header: 'Abonnés', numeric: true, render: (row) => formatInteger(row.totalSubscribers) },
  { key: 'annual', header: 'Annuels', numeric: true, render: (row) => formatInteger(row.annualSubscribers) },
  { key: 'monthly', header: 'Mensuels', numeric: true, render: (row) => formatInteger(row.monthlySubscribers) },
  { key: 'pen', header: 'Pénétration', numeric: true, render: (row) => formatPercent(row.penetrationPercent) },
];

/**
 * `unit: 'percent'` : l'écart s'annonce en POINTS et non en variation
 * relative. « La pénétration a gagné 8 % » se lit comme 8 points par la moitié
 * des lecteurs, alors qu'il s'agirait de 1,7 point sur une base de 21 %.
 *
 * Mode `level` malgré l'allure lisse de la courbe : un taux est un rapport à
 * un instant donné, pas un cumul. Sa valeur d'un jour ne contient pas celle de
 * la veille.
 */
const MARKER_CONFIG = {
  value: (row: PenetrationPoint) => row.penetrationPercent,
  mode: 'level',
  unit: 'percent',
  higherIsBetter: true,
  scope: 'analytics',
} as const;

export function PenetrationChart({ data }: { data: readonly PenetrationPoint[] }) {
  const markers = useChartMarkers(data, MARKER_CONFIG);

  return (
    <ChartCard
      title="Taux de pénétration"
      subtitle="Abonnés payants rapportés aux comptes existants — mesuré, pas reconstitué"
      emptyMessage="Aucun snapshot sur cette période. Le premier arrive au lendemain du premier sync."
      className="chart-grid__wide"
      rows={data}
      columns={columns}
      chartHeight={300}
      footer={<MarkerStrip items={markers.items} />}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data as PenetrationPoint[]} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDayShort}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            // Echelle calee sur les donnees et non sur 0-100 : un taux qui
            // evolue entre 18 et 22 % serait une droite plate sur un axe
            // complet. Le sous-titre precise ce qui est mesure.
            domain={[
              (min: number) => Math.max(0, Math.floor(min - 1)),
              (max: number) => Math.ceil(max + 1),
            ]}
            tickFormatter={(value: number) => `${value} %`}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={<ChartTooltip labelFormatter={formatDayLong} valueFormatter={formatPercent} />}
          />
          {markers.lines}
          <Line
            isAnimationActive={false}
            type="monotone"
            dataKey="penetrationPercent"
            name="Pénétration"
            stroke={COLORS.series1}
            strokeWidth={LINE_WIDTH}
            dot={false}
            connectNulls={false}
            activeDot={{ r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
